import type { NextPage } from 'next'
import Image from 'next/image'
import Link from 'next/link'
import { useState, useEffect, useRef, useMemo } from 'react'
import Contact from '../../components/contact/Contact'
import Item from '../../components/item/Item'
import ImageQuote from '../../components/imagequote/ImageQuote'
import CustomImage from '../../components/customImage/CustomImage'

import styles from '../../styles/pages/Faq.module.scss'

import { gsap } from 'gsap'

import CustomMetaData from '../../components/general/customMetaData/CustomMetaData'

import { GenericPageLayoutProps } from '../../components/general/genericPageLayout/GenericPageLayout'

import { useGlobalContentContext } from '../../context/GlobalContentContext'

import { parseGenericPage } from '../../utils/parsers'
import { getAPI, createMarkup } from '../../utils/helpers'

type QuestionProps = {
  question: any
  onActiveChange: any
  active: boolean
}
const Question = (props: QuestionProps) => {
  const answerRef = useRef<HTMLDivElement | null>(null)
  const answerRefInner = useRef<HTMLDivElement | null>(null)
  const { question, active } = props

  const t1 = useMemo(() => gsap.timeline({ paused: true }), [])

  useEffect(() => {
    if (answerRef.current && answerRefInner.current) {
      const naturalHeight =
        answerRefInner.current.getBoundingClientRect().height

      t1.fromTo(
        answerRef.current,
        { height: 0 },
        { height: naturalHeight, duration: 0.25, ease: 'linear' }
      )

      t1.fromTo(
        answerRefInner.current,
        { opacity: 0 },
        {
          opacity: 1,
          duration: 0.25,
          ease: 'linear',
        }
      )
    }
    return () => {
      t1 && t1.kill()
    }
  }, [answerRef, answerRefInner, t1])

  useEffect(() => {
    if (active) {
      t1.play()
    } else {
      t1.reverse()
    }
  }, [active, t1])

  const handleClick = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ): void => {
    props.onActiveChange(e)
  }

  return (
    <div className={styles.faqItem} id={`${question.id}`}>
      <div className={styles.question}>
        <a
          onClick={(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void =>
            handleClick(e)
          }
          href={`#${question.slug}`}
        >
          {question.question}
        </a>
      </div>

      <div ref={answerRef} className={styles.answerContainer}>
        <div
          ref={answerRefInner}
          id={question.id}
          className={styles.answer}
          dangerouslySetInnerHTML={createMarkup(question.answer)}
        ></div>
      </div>
    </div>
  )
}

type Props = {
  categories: any
} & GenericPageLayoutProps

const Faq = (props: Props) => {
  const { setData } = useGlobalContentContext()

  const [activeId, setActiveId] = useState(-1)

  const handleActiveChange = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    id: number
  ): void => {
    e.preventDefault()
    if (id != activeId) {
      setActiveId(id)
    } else {
      setActiveId(-1)
    }
  }

  useEffect(() => {
    setData(props.globalData)
  }, [props.globalData, setData])

  return (
    <div className={styles.container}>
      <CustomMetaData
        title={props.originalResponse.meta_title}
        description={props.originalResponse.meta_description}
        image={props.originalResponse.meta_image}
      />

      <div className={styles.main}>
        <section className={styles.hero}>
          <div className={styles.heroContentContainer}>
            <h1 className={styles.heroTitle}>{props.originalResponse.title}</h1>
          </div>
          <div className={styles.heroImage}>
            <CustomImage
              src={props.originalResponse.hero}
              layout="fill"
              objectFit="cover"
            />
          </div>
        </section>

        <section className={styles.faqContainer}>
          <div className={styles.faqSection}>
            {props.categories.map((category: any, index: number) => (
              <div key={index}>
                <h2 className={styles.categoryTitle}>{category.title}</h2>
                <div className={styles.questionsContainer}>
                  {category.questions
                    .filter((item: any) => item.category.id == category.id)
                    .map((question: any, question_index: number) => (
                      <Question
                        key={question.id}
                        question={question}
                        onActiveChange={(
                          e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
                        ): void => handleActiveChange(e, question.id)}
                        active={activeId == question.id}
                      />
                    ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        <Contact />
      </div>
    </div>
  )
}

export async function getStaticProps() {
  const page: GenericPageLayoutProps = await parseGenericPage(
    `${getAPI()}/faqpage/`
  )

  const categoriesRequest = await fetch(`${getAPI()}/question-categories/`)
  const categories = await categoriesRequest.json()
  return {
    props: {
      originalResponse: page.originalResponse,
      title: page.title,
      categories: categories,
      globalData: page.globalData,
      meta_title: page.meta_title,
      meta_description: page.meta_description,
    },
    revalidate: NEXTJS_REVALIDATE_PAGE_IN_SECONDS,
  }
}

export const NEXTJS_REVALIDATE_PAGE_IN_SECONDS = 10
export default Faq
